import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppContext } from '../context/AppContext';
import { useActiveSection } from '../hooks/useActiveSection';
import { Language, ThemeType } from '../types';
import { Container, ease, hover, pressable, radius, text } from '../styles/GlobalStyles';
import { CloseIcon, MenuIcon, MonitorIcon, MoonIcon, SunIcon } from './common/Icons';

const SECTIONS = ['home', 'about', 'experience', 'skills', 'projects', 'contact'] as const;

const Bar = styled.header<{ $scrolled: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  background: ${props => props.theme.colors.overlay};
  backdrop-filter: saturate(180%) blur(14px);
  -webkit-backdrop-filter: saturate(180%) blur(14px);
  border-bottom: 1px solid ${props => (props.$scrolled ? props.theme.colors.border : 'transparent')};
  transition: border-color 0.2s ease;
`;

const Inner = styled(Container)`
  display: flex;
  align-items: center;
  gap: 24px;
  height: 60px;
`;

const Wordmark = styled.button`
  border: none;
  background: none;
  padding: 0;
  margin-right: auto;
  font-size: ${text.sm};
  font-weight: 600;
  letter-spacing: -0.01em;
  color: ${props => props.theme.colors.text};
  cursor: pointer;

  span {
    color: ${props => props.theme.colors.textTertiary};
    font-weight: 400;
  }

  @media (max-width: 420px) {
    span {
      display: none;
    }
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 4px;

  @media (max-width: 900px) {
    display: none;
  }
`;

const NavLink = styled.button<{ $active: boolean }>`
  position: relative;
  height: 60px;
  padding: 0 10px;
  border: none;
  background: none;
  font-size: ${text.sm};
  font-weight: 500;
  cursor: pointer;
  color: ${props => (props.$active ? props.theme.colors.text : props.theme.colors.textSecondary)};
  transition: color 150ms ease;

  ${hover} {
    &:hover {
      color: ${props => props.theme.colors.text};
    }
  }
`;

const Indicator = styled(motion.span)`
  position: absolute;
  left: 10px;
  right: 10px;
  bottom: -1px;
  height: 2px;
  background: ${props => props.theme.colors.primary};
`;

const Controls = styled.div`
  position: relative;
`;

const IconButton = styled.button`
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  border-radius: ${radius.md};
  border: 1px solid ${props => props.theme.colors.border};
  background: transparent;
  color: ${props => props.theme.colors.text};
  cursor: pointer;
  ${pressable}

  ${hover} {
    &:hover {
      background: ${props => props.theme.colors.backgroundAlt};
    }
  }
`;

const Popover = styled(motion.div)`
  position: absolute;
  top: calc(100% + 10px);
  right: 0;
  width: 264px;
  padding: 14px;
  border-radius: ${radius.lg};
  background: ${props => props.theme.colors.cardBackground};
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${props => props.theme.shadows.large};
  transform-origin: top right;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const Label = styled.div`
  margin-bottom: 8px;
  font-size: ${text.xs};
  color: ${props => props.theme.colors.textTertiary};
`;

const Segmented = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: 1fr;
  padding: 2px;
  border-radius: 9px;
  background: ${props => props.theme.colors.backgroundAlt};
`;

const Segment = styled.button<{ $active: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 30px;
  border: none;
  background: none;
  font-size: ${text.xs};
  font-weight: 500;
  cursor: pointer;
  color: ${props => (props.$active ? props.theme.colors.text : props.theme.colors.textSecondary)};

  span,
  svg {
    position: relative;
    z-index: 1;
  }
`;

const Thumb = styled(motion.div)`
  position: absolute;
  inset: 0;
  border-radius: 7px;
  background: ${props => props.theme.colors.cardBackground};
  box-shadow: ${props => props.theme.shadows.medium};
`;

const MobileLinks = styled.div`
  display: none;
  flex-direction: column;
  padding-bottom: 14px;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  @media (max-width: 900px) {
    display: flex;
  }
`;

const MobileLink = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  padding: 0 4px;
  border: none;
  background: none;
  font-size: ${text.base};
  font-weight: 500;
  text-align: left;
  cursor: pointer;
  color: ${props => (props.$active ? props.theme.colors.text : props.theme.colors.textSecondary)};

  &::before {
    content: '';
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${props => (props.$active ? props.theme.colors.primary : 'transparent')};
  }
`;

const themeOptions: { value: ThemeType; icon: React.ReactNode; en: string; tr: string }[] = [
  { value: 'light', icon: <SunIcon size={13} />, en: 'Light', tr: 'Açık' },
  { value: 'dark', icon: <MoonIcon size={13} />, en: 'Dark', tr: 'Koyu' },
  { value: 'system', icon: <MonitorIcon size={13} />, en: 'Auto', tr: 'Oto' },
];

const languageOptions: { value: Language; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'tr', label: 'Türkçe' },
];

const snap = { type: 'spring' as const, duration: 0.3, bounce: 0 };

const Header: React.FC = () => {
  const { theme, setTheme, language, setLanguage, content } = useAppContext();
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const controlsRef = useRef<HTMLDivElement>(null);
  const active = useActiveSection([...SECTIONS]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    // composedPath is fixed at dispatch, so it still holds the menu icon the open click just swapped out of the DOM.
    const close = (event: MouseEvent) => {
      if (controlsRef.current && !event.composedPath().includes(controlsRef.current)) setIsOpen(false);
    };
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setIsOpen(false);
    document.addEventListener('click', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('click', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsOpen(false);
  };

  const [firstName, ...rest] = content.personalInfo.name.split(' ');

  return (
    <Bar $scrolled={scrolled}>
      <Inner>
        <Wordmark onClick={() => scrollTo('home')}>
          {firstName} <span>{rest.join(' ')}</span>
        </Wordmark>

        <Nav>
          {SECTIONS.map(id => (
            <NavLink key={id} $active={active === id} onClick={() => scrollTo(id)}>
              {content.navigation[id]}
              {active === id && <Indicator layoutId="nav-indicator" transition={snap} />}
            </NavLink>
          ))}
        </Nav>

        <Controls ref={controlsRef}>
          <IconButton onClick={() => setIsOpen(open => !open)} aria-label="Settings" aria-expanded={isOpen}>
            {isOpen ? <CloseIcon size={15} /> : <MenuIcon size={15} />}
          </IconButton>

          <AnimatePresence>
            {isOpen && (
              <Popover
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1, transition: { duration: 0.16, ease: ease.out } }}
                exit={{ opacity: 0, scale: 0.98, transition: { duration: 0.12, ease: ease.out } }}
              >
                <MobileLinks>
                  {SECTIONS.map(id => (
                    <MobileLink key={id} $active={active === id} onClick={() => scrollTo(id)}>
                      {content.navigation[id]}
                    </MobileLink>
                  ))}
                </MobileLinks>

                <div>
                  <Label>{language === 'tr' ? 'Görünüm' : 'Appearance'}</Label>
                  <Segmented>
                    {themeOptions.map(option => (
                      <Segment key={option.value} $active={theme === option.value} onClick={() => setTheme(option.value)}>
                        {theme === option.value && <Thumb layoutId="theme-thumb" transition={snap} />}
                        {option.icon}
                        <span>{option[language]}</span>
                      </Segment>
                    ))}
                  </Segmented>
                </div>

                <div>
                  <Label>{language === 'tr' ? 'Dil' : 'Language'}</Label>
                  <Segmented>
                    {languageOptions.map(option => (
                      <Segment key={option.value} $active={language === option.value} onClick={() => setLanguage(option.value)}>
                        {language === option.value && <Thumb layoutId="lang-thumb" transition={snap} />}
                        <span>{option.label}</span>
                      </Segment>
                    ))}
                  </Segmented>
                </div>
              </Popover>
            )}
          </AnimatePresence>
        </Controls>
      </Inner>
    </Bar>
  );
};

export default Header;
